import "./UserDetailsPage.css"
import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { AppDispatch } from "../../redux/store";
import { fetchUserSpas } from "../../redux/actions/spas.actions";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import EditUserModal from "../../components/common/Modals/EditUserModal";
import SpasTable from "../../components/common/Tables/SpasTable";
import { fetchUserById } from "../../redux/actions/users.actions";
import { PencilIcon } from "../../components/common/Icons/Icons";
import NotFound from "../General/NotFound";
import EditSpaModal from "../../components/common/Modals/EditSpaModal";
import DeleteSpaModal from "../../components/common/Modals/DeleteSpaModal";
import { fetchDealership } from "../../redux/actions/dealership.actions";
import EditRoleModal from "./components/EditRoleModal";
import usePermissionCheck from "../../hooks/usePermissionCheck";

const UserDetailsPage: React.FC = () => {
  // Data
  const {id} = useParams();
  const dispatch: AppDispatch = useDispatch();
  const {data:user, error, loading} = useSelector((state: RootState) => state.usersById);
  const {data:dealership, loading:dealershipLoading} = useSelector((state: RootState) => state.dealership);
  const isLoading = [loading, dealershipLoading]
  const searchParams = useSearchParams()[0];

  //Modal Handling
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editSpaOpen, setEditSpaOpen] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);

  const [spaToModify, setSpaToModify] = useState<any>(null);

  const {canAccess:canEditUser} = usePermissionCheck(['update_user']);
  const {canAccess:canEditRoles} = usePermissionCheck(['assign_sales_role', 'assign_technician_role', 'assign_customer_role']);

  // Check if the user being edited has an allowed role (technician, sales, customer, or null)
  const canEditTargetUserRoles = user?.roles?.some((role: any) => 
    ['technician', 'sales', 'customer'].includes(role.name)
  ) || user?.roles?.length === 0 || !user?.roles;

  // Combined check: current user can edit roles AND target user has allowed role
  const canEditRolesFinal = canEditRoles && canEditTargetUserRoles;

  useEffect(() => {
    if (id) {
      dispatch(fetchUserById(id));
      dispatch(fetchUserSpas({page:searchParams.toString(), uuid:id}));
    }
  }, [dispatch,id,searchParams]);

  useEffect(() => {
    if (user && user?.dealership_id) {
      dispatch(fetchDealership(user?.dealership_id));
    }
  }, [user, dispatch]);

  if (error){
    return <NotFound/>
  }
  
  const location = [user?.city, user?.region].filter(Boolean).join(', ');

  return (
    <div className="page lp-page">
    <EditUserModal open={editOpen} setOpen={setEditOpen} object_id={user?.id}/>
    <DeleteSpaModal open={deleteOpen} setOpen={setDeleteOpen} object={spaToModify}/>
    <EditSpaModal open={editSpaOpen} setOpen={setEditSpaOpen} object_id={spaToModify?.id}/>
    <EditRoleModal open={editRoleOpen} setOpen={setEditRoleOpen} object_id={user?.id}/>

    <Header header="User Details"/>
    <main className="lp-main">
      {isLoading.includes(true) ?
        <div className="lp-loading"><span className="spinner" role="progressbar" aria-label="Loading user"/></div>
      :
        <div className="lp-layout">
          <aside className="lp-side" aria-label="User">
            <section className="lp-side-section">
              <div className="lp-side-head">
                <h1 className="lp-side-name">{user?.name}</h1>
                {canEditUser &&
                  <button type="button" className="lp-side-btn" onClick={()=>setEditOpen(true)} aria-label="Edit user"><PencilIcon/></button>}
              </div>
              <dl className="lp-facts">
                <div><dt>Username</dt><dd>{user?.username}</dd></div>
                <div><dt>Email</dt><dd>{user?.email}</dd></div>
                {user?.phone && <div><dt>Phone</dt><dd>{user.phone}</dd></div>}
                {location && <div><dt>Location</dt><dd>{location}</dd></div>}
              </dl>
            </section>

            <section className="lp-side-section">
              <div className="lp-side-head">
                <h2 className="lp-side-title">Roles</h2>
                {canEditRolesFinal &&
                  <button type="button" className="lp-side-btn" onClick={()=>setEditRoleOpen(true)} aria-label="Edit roles"><PencilIcon/></button>}
              </div>
              <div className="lp-chips">
                {user?.roles!==undefined && user?.roles.length>0 ?
                  user.roles.map((r:any, i:number)=>
                    <span key={i} className="lp-chip">{r.name}</span>
                  )
                  :
                  <span className="lp-muted">None</span>
                }
              </div>
            </section>

            <section className="lp-side-section">
              <div className="lp-side-head">
                <h2 className="lp-side-title">Dealership</h2>
                {user?.dealership_id && <span className="lp-muted">#{dealership?.id}</span>}
              </div>
              {dealershipLoading ? <span className="spinner lp-side-spinner" role="progressbar" aria-label="Loading dealership"/>
              :
              user?.dealership_id && dealership ?
              <div className="lp-stack">
                <span className="lp-strong">{dealership?.name}</span>
                <span className="lp-muted">{dealership?.city}, {dealership?.region?.label}, {dealership?.country?.title}</span>
              </div>
              :
              <span className="lp-muted">None</span>
              }
            </section>
          </aside>

          <SpasTable
            pageName={`user-details`}
            setEditOpen={setEditSpaOpen}
            setDeleteOpen={setDeleteOpen}
            setSpaToModify={setSpaToModify}
          />
        </div>
      }
    </main>
    </div>
  );
};

export default UserDetailsPage;
